export type Unit = {
  name: string;
  symbol: string;
  toBase: (val: number) => number;
  fromBase: (val: number) => number;
};

export type ConverterCategory = {
  name: string;
  baseUnit: string;
  units: Record<string, Unit>;
};

export const CONVERTER_CONFIG: Record<string, ConverterCategory> = {
  length: {
    name: "Length",
    baseUnit: 'm',
    units: {
      m: { name: 'Meter', symbol: 'm', toBase: v => v, fromBase: v => v },
      km: { name: 'Kilometer', symbol: 'km', toBase: v => v * 1000, fromBase: v => v / 1000 },
      cm: { name: 'Centimeter', symbol: 'cm', toBase: v => v / 100, fromBase: v => v * 100 },
      mm: { name: 'Millimeter', symbol: 'mm', toBase: v => v / 1000, fromBase: v => v * 1000 },
      mi: { name: 'Mile', symbol: 'mi', toBase: v => v * 1609.34, fromBase: v => v / 1609.34 },
      yd: { name: 'Yard', symbol: 'yd', toBase: v => v * 0.9144, fromBase: v => v / 0.9144 },
      ft: { name: 'Foot', symbol: 'ft', toBase: v => v * 0.3048, fromBase: v => v / 0.3048 },
      in: { name: 'Inch', symbol: 'in', toBase: v => v * 0.0254, fromBase: v => v / 0.0254 },
    }
  },
  area: {
    name: "Area",
    baseUnit: 'm2',
    units: {
      sqm: { name: 'Square Meter', symbol: 'm²', toBase: v => v, fromBase: v => v },
      sqkm: { name: 'Square Kilometer', symbol: 'km²', toBase: v => v * 1e6, fromBase: v => v / 1e6 },
      sqcm: { name: 'Square Centimeter', symbol: 'cm²', toBase: v => v / 1e4, fromBase: v => v * 1e4 },
      ha: { name: 'Hectare', symbol: 'ha', toBase: v => v * 1e4, fromBase: v => v / 1e4 },
      sqft: { name: 'Square Foot', symbol: 'ft²', toBase: v => v * 0.092903, fromBase: v => v / 0.092903 },
      sqin: { name: 'Square Inch', symbol: 'in²', toBase: v => v * 0.00064516, fromBase: v => v / 0.00064516 },
      acre: { name: 'Acre', symbol: 'ac', toBase: v => v * 4046.86, fromBase: v => v / 4046.86 },
    }
  },
  mass: {
    name: "Mass",
    baseUnit: 'g',
    units: {
      g: { name: 'Gram', symbol: 'g', toBase: v => v, fromBase: v => v },
      kg: { name: 'Kilogram', symbol: 'kg', toBase: v => v * 1000, fromBase: v => v / 1000 },
      mg: { name: 'Milligram', symbol: 'mg', toBase: v => v / 1000, fromBase: v => v * 1000 },
      t: { name: 'Tonne', symbol: 't', toBase: v => v * 1e6, fromBase: v => v / 1e6 },
      lb: { name: 'Pound', symbol: 'lb', toBase: v => v * 453.592, fromBase: v => v / 453.592 },
      oz: { name: 'Ounce', symbol: 'oz', toBase: v => v * 28.3495, fromBase: v => v / 28.3495 },
    }
  },
  volume: {
    name: "Volume",
    baseUnit: 'l',
    units: {
      l: { name: 'Liter', symbol: 'l', toBase: v => v, fromBase: v => v },
      ml: { name: 'Milliliter', symbol: 'ml', toBase: v => v / 1000, fromBase: v => v * 1000 },
      m3: { name: 'Cubic Meter', symbol: 'm³', toBase: v => v * 1000, fromBase: v => v / 1000 },
      gal: { name: 'US Gallon', symbol: 'gal', toBase: v => v * 3.78541, fromBase: v => v / 3.78541 },
      qt: { name: 'US Quart', symbol: 'qt', toBase: v => v * 0.946353, fromBase: v => v / 0.946353 },
      pt: { name: 'US Pint', symbol: 'pt', toBase: v => v * 0.473176, fromBase: v => v / 0.473176 },
      floz: { name: 'US Fluid Ounce', symbol: 'fl oz', toBase: v => v * 0.0295735, fromBase: v => v / 0.0295735 },
    }
  },
  temperature: {
    name: "Temperature",
    baseUnit: 'c',
    units: {
      c: { name: 'Celsius', symbol: '°C', toBase: v => v, fromBase: v => v },
      f: { name: 'Fahrenheit', symbol: '°F', toBase: v => (v - 32) * 5/9, fromBase: v => (v * 9/5) + 32 },
      k: { name: 'Kelvin', symbol: 'K', toBase: v => v - 273.15, fromBase: v => v + 273.15 },
    }
  },
  angle: {
    name: "Angle",
    baseUnit: 'deg',
    units: {
        deg: { name: 'Degree', symbol: '°', toBase: v => v, fromBase: v => v },
        rad: { name: 'Radian', symbol: 'rad', toBase: v => v * 180 / Math.PI, fromBase: v => v * Math.PI / 180 },
        grad: { name: 'Gradian', symbol: 'grad', toBase: v => v * 0.9, fromBase: v => v / 0.9 },
    }
  },
  time: {
    name: "Time",
    baseUnit: 's',
    units: {
        s: { name: 'Second', symbol: 's', toBase: v => v, fromBase: v => v },
        ms: { name: 'Millisecond', symbol: 'ms', toBase: v => v / 1000, fromBase: v => v * 1000 },
        min: { name: 'Minute', symbol: 'min', toBase: v => v * 60, fromBase: v => v / 60 },
        h: { name: 'Hour', symbol: 'h', toBase: v => v * 3600, fromBase: v => v / 3600 },
        day: { name: 'Day', symbol: 'd', toBase: v => v * 86400, fromBase: v => v / 86400 },
        week: { name: 'Week', symbol: 'wk', toBase: v => v * 604800, fromBase: v => v / 604800 },
    }
  },
  digital: {
    name: "Digital Storage",
    baseUnit: 'B',
    units: {
        B: { name: 'Byte', symbol: 'B', toBase: v => v, fromBase: v => v },
        KB: { name: 'Kilobyte', symbol: 'KB', toBase: v => v * 1024, fromBase: v => v / 1024 },
        MB: { name: 'Megabyte', symbol: 'MB', toBase: v => v * Math.pow(1024, 2), fromBase: v => v / Math.pow(1024, 2) },
        GB: { name: 'Gigabyte', symbol: 'GB', toBase: v => v * Math.pow(1024, 3), fromBase: v => v / Math.pow(1024, 3) },
        TB: { name: 'Terabyte', symbol: 'TB', toBase: v => v * Math.pow(1024, 4), fromBase: v => v / Math.pow(1024, 4) },
        b: { name: 'Bit', symbol: 'b', toBase: v => v / 8, fromBase: v => v * 8 },
    }
  },
  partsPer: {
    name: "Parts-Per",
    baseUnit: 'ppm',
    units: {
        ppm: { name: 'Parts-per-million', symbol: 'ppm', toBase: v => v, fromBase: v => v },
        ppb: { name: 'Parts-per-billion', symbol: 'ppb', toBase: v => v / 1000, fromBase: v => v * 1000 },
        ppt: { name: 'Parts-per-trillion', symbol: 'ppt', toBase: v => v / 1e6, fromBase: v => v * 1e6 },
        ppq: { name: 'Parts-per-quadrillion', symbol: 'ppq', toBase: v => v / 1e9, fromBase: v => v * 1e9 },
    }
  },
  speed: {
    name: "Speed",
    baseUnit: 'm/s',
    units: {
        'm/s': { name: 'Meter/second', symbol: 'm/s', toBase: v => v, fromBase: v => v },
        'km/h': { name: 'Kilometer/hour', symbol: 'km/h', toBase: v => v / 3.6, fromBase: v => v * 3.6 },
        mph: { name: 'Mile/hour', symbol: 'mph', toBase: v => v / 2.237, fromBase: v => v * 2.237 },
        knot: { name: 'Knot', symbol: 'kn', toBase: v => v / 1.944, fromBase: v => v * 1.944 },
    }
  },
  pace: {
    name: "Pace",
    baseUnit: 's/m',
    units: {
        's/m': { name: 'Second/meter', symbol: 's/m', toBase: v => v, fromBase: v => v },
        'min/km': { name: 'Minute/kilometer', symbol: 'min/km', toBase: v => v * 0.06, fromBase: v => v / 0.06 },
        'min/mi': { name: 'Minute/mile', symbol: 'min/mi', toBase: v => v * 0.0372823, fromBase: v => v / 0.0372823 },
    }
  },
  pressure: {
    name: "Pressure",
    baseUnit: 'Pa',
    units: {
        Pa: { name: 'Pascal', symbol: 'Pa', toBase: v => v, fromBase: v => v },
        kPa: { name: 'Kilopascal', symbol: 'kPa', toBase: v => v * 1000, fromBase: v => v / 1000 },
        bar: { name: 'Bar', symbol: 'bar', toBase: v => v * 100000, fromBase: v => v / 100000 },
        psi: { name: 'Pound-force/sq-inch', symbol: 'psi', toBase: v => v * 6894.76, fromBase: v => v / 6894.76 },
        atm: { name: 'Atmosphere', symbol: 'atm', toBase: v => v * 101325, fromBase: v => v / 101325 },
    }
  },
  current: {
    name: "Current",
    baseUnit: 'A',
    units: {
        A: { name: 'Ampere', symbol: 'A', toBase: v => v, fromBase: v => v },
        mA: { name: 'Milliampere', symbol: 'mA', toBase: v => v / 1000, fromBase: v => v * 1000 },
        kA: { name: 'Kiloampere', symbol: 'kA', toBase: v => v * 1000, fromBase: v => v / 1000 },
    }
  },
  voltage: {
    name: "Voltage",
    baseUnit: 'V',
    units: {
        V: { name: 'Volt', symbol: 'V', toBase: v => v, fromBase: v => v },
        mV: { name: 'Millivolt', symbol: 'mV', toBase: v => v / 1000, fromBase: v => v * 1000 },
        kV: { name: 'Kilovolt', symbol: 'kV', toBase: v => v * 1000, fromBase: v => v / 1000 },
    }
  },
  power: {
    name: "Power",
    baseUnit: 'W',
    units: {
        W: { name: 'Watt', symbol: 'W', toBase: v => v, fromBase: v => v },
        kW: { name: 'Kilowatt', symbol: 'kW', toBase: v => v * 1000, fromBase: v => v / 1000 },
        MW: { name: 'Megawatt', symbol: 'MW', toBase: v => v * 1e6, fromBase: v => v / 1e6 },
        hp: { name: 'Horsepower', symbol: 'hp', toBase: v => v * 745.7, fromBase: v => v / 745.7 },
    }
  },
  reactivePower: {
    name: "Reactive Power",
    baseUnit: 'VAR',
    units: {
        VAR: { name: 'Volt-Ampere Reactive', symbol: 'VAR', toBase: v => v, fromBase: v => v },
        kVAR: { name: 'Kilovolt-Ampere Reactive', symbol: 'kVAR', toBase: v => v * 1000, fromBase: v => v / 1000 },
        MVAR: { name: 'Megavolt-Ampere Reactive', symbol: 'MVAR', toBase: v => v * 1e6, fromBase: v => v / 1e6 },
    }
  },
  apparentPower: {
    name: "Apparent Power",
    baseUnit: 'VA',
    units: {
        VA: { name: 'Volt-Amperes', symbol: 'VA', toBase: v => v, fromBase: v => v },
        kVA: { name: 'Kilovolt-Amperes', symbol: 'kVA', toBase: v => v * 1000, fromBase: v => v / 1000 },
        MVA: { name: 'Megavolt-Amperes', symbol: 'MVA', toBase: v => v * 1e6, fromBase: v => v / 1e6 },
    }
  },
  energy: {
    name: "Energy",
    baseUnit: 'J',
    units: {
        J: { name: 'Joule', symbol: 'J', toBase: v => v, fromBase: v => v },
        kJ: { name: 'Kilojoule', symbol: 'kJ', toBase: v => v * 1000, fromBase: v => v / 1000 },
        Wh: { name: 'Watt-hour', symbol: 'Wh', toBase: v => v * 3600, fromBase: v => v / 3600 },
        kWh: { name: 'Kilowatt-hour', symbol: 'kWh', toBase: v => v * 3.6e6, fromBase: v => v / 3.6e6 },
        cal: { name: 'Calorie', symbol: 'cal', toBase: v => v * 4.184, fromBase: v => v / 4.184 },
    }
  },
  reactiveEnergy: {
    name: "Reactive Energy",
    baseUnit: 'VARh',
    units: {
        VARh: { name: 'Volt-Ampere Reactive Hour', symbol: 'VARh', toBase: v => v, fromBase: v => v },
        kVARh: { name: 'Kilovolt-Ampere Reactive Hour', symbol: 'kVARh', toBase: v => v * 1000, fromBase: v => v / 1000 },
        MVARh: { name: 'Megavolt-Ampere Reactive Hour', symbol: 'MVARh', toBase: v => v * 1e6, fromBase: v => v / 1e6 },
    }
  },
  flowRate: {
    name: "Volumetric Flow Rate",
    baseUnit: 'm3/s',
    units: {
        'm3/s': { name: 'Cubic Meter/Second', symbol: 'm³/s', toBase: v => v, fromBase: v => v },
        'L/s': { name: 'Liter/Second', symbol: 'L/s', toBase: v => v / 1000, fromBase: v => v * 1000 },
        'ft3/min': { name: 'Cubic Foot/Minute', symbol: 'ft³/min', toBase: v => v / 2118.88, fromBase: v => v * 2118.88 },
        'gal/min': { name: 'Gallon/Minute (US)', symbol: 'gpm', toBase: v => v * 0.00006309, fromBase: v => v / 0.00006309 },
    }
  },
  illuminance: {
    name: "Illuminance",
    baseUnit: 'lx',
    units: {
        lx: { name: 'Lux', symbol: 'lx', toBase: v => v, fromBase: v => v },
        fc: { name: 'Foot-candle', symbol: 'fc', toBase: v => v * 10.764, fromBase: v => v / 10.764 },
    }
  },
};
